// src/services/adjustmentService.js
//
// CRUD + validation for standalone adjustment entries
// (companies/{cid}/adjustments — PRD_StandaloneAdjustments C1/C11/C12/C14/C18).
// Both creation surfaces (the Adjustments page and the payroll wizard's Adjustments step) write
// through this service so the documents are identical (C11).
//
// Date discipline (C2): effectiveDate is a 'YYYY-MM-DD' string, serialized via
// toYMDString on EVERY boundary — inputs may arrive as Firestore Timestamp |
// Date | string and must never reach Firestore or a string compare raw.
//
// The consumption link (consumedRunId/consumedAt) is written ONLY server-side
// (savePayrollRun / deletePayrollRun); this service always writes it null on
// create and never touches it on update — the rules pin enforces that.

import {
  collection,
  doc,
  addDoc,
  updateDoc,
  getDoc,
  getDocs,
  query,
  where,
  serverTimestamp,
  writeBatch,
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { toYMDString } from '../utils/periodDate';
import { formatPeso } from '../utils/currency';
import { currentPeriod, officialPeriods } from '../utils/payCutoffs';
import { ADJ_STATUS, resolveAdjustmentStatus, isUnpaidStatus } from './adjustmentDerivation';
import { loadActiveLocks } from './dtrLockDerivation';

// Category lists — single source for both surfaces (C11); previously hardcoded
// in ManualAdjustments.js (PRD_Adjustments' 8 + 8 lists, verbatim).
export const EARNING_CATEGORIES = [
  { value: 'allowance', label: 'Allowance' },
  { value: 'bonus', label: 'Bonus' },
  { value: 'commission', label: 'Commission' },
  { value: 'incentive', label: 'Incentive' },
  { value: 'reimbursement', label: 'Reimbursement' },
  { value: 'thirteenth_month', label: '13th Month Pay' },
  { value: 'back_pay', label: 'Back Pay' },
  { value: 'other_earning', label: 'Other Earning' },
];

export const DEDUCTION_CATEGORIES = [
  { value: 'dues', label: 'Dues' },
  { value: 'groceries', label: 'Groceries' },
  { value: 'loan', label: 'Loan Payment' },
  { value: 'cash_advance', label: 'Cash Advance' },
  { value: 'damages', label: 'Damages' },
  { value: 'tardiness', label: 'Tardiness' },
  { value: 'absence', label: 'Absence' },
  { value: 'other_deduction', label: 'Other Deduction' },
];

// Retired from the pickers above but still stored on existing adjustments (UAT
// holds 2 'uniform' rows), and every label lookup falls back to the RAW value —
// so without these the saved rows would render as lowercase 'uniform'. Not
// selectable for new adjustments; display only.
export const LEGACY_CATEGORIES = [
  { value: 'uniform', label: 'Uniform' },
  { value: 'equipment', label: 'Equipment' },
];

export const adjustmentsCollection = (companyId) =>
  collection(db, 'companies', companyId, 'adjustments');

// ============================================================
// C12 validators (pure — exported for the Tier-1 suite)
// ============================================================

/**
 * C12(a) — employee tenure, a HARD block. Compares 'YYYY-MM-DD' strings against
 * the employee's CURRENT employment dates (the caller passes the live employee
 * doc's employment object); empty dates mean no constraint. Reuses the
 * comparison shape of employeeFilters.js (never raw Date instants), and
 * absorbs Timestamp | Date | string on every input (C2).
 *
 * @param {*} effectiveDate - Timestamp | Date | 'YYYY-MM-DD'
 * @param {Object} employment - { startDate?, endDate? } from the employee doc
 * @returns {string|null} error message, or null when valid
 */
export const validateTenure = (effectiveDate, employment = {}) => {
  const dateYMD = toYMDString(effectiveDate);
  if (!dateYMD) return 'Effective date is required.';
  const startYMD = toYMDString(employment.startDate);
  const endYMD = toYMDString(employment.endDate);
  if (startYMD && dateYMD < startYMD) {
    return `Hired after this date (employment starts ${startYMD}).`;
  }
  if (endYMD && dateYMD > endYMD) {
    return `Employment ended before this date (ended ${endYMD}).`;
  }
  return null;
};

/**
 * C12(b) — amount, a HARD block: a finite number > 0 at 2 decimal places. The
 * sign is carried by `type`, never the value — a negative "deduction" would
 * INCREASE net pay and print as an ordinary positive payslip line.
 *
 * @param {*} amount
 * @returns {string|null} error message, or null when valid
 */
export const validateAmount = (amount) => {
  if (typeof amount !== 'number' || !Number.isFinite(amount) || amount <= 0) {
    return 'Amount must be a number greater than zero.';
  }
  // Float-safe 2dp check: `amount * 100` is NOT an exact integer for most
  // 2dp values in IEEE-754 (19.99 * 100 === 1998.999…), so comparing against
  // Math.round rejects ordinary peso amounts. Round-tripping through toFixed
  // compares the value against its nearest 2dp representation instead.
  if (Number(amount.toFixed(2)) !== amount) {
    return 'Amount cannot have more than 2 decimal places.';
  }
  return null;
};

/**
 * C12(c) — already-paid period, a WARNING only (advisory period coverage;
 * client-only, C22). A pending-approval/completed run covering this
 * employee+date means the employee is hard-excluded from any new run over that
 * date, so under C13 the entry could never be picked up — the copy states that
 * consequence plainly.
 *
 * @param {string} employeeId
 * @param {*} effectiveDate - Timestamp | Date | 'YYYY-MM-DD'
 * @param {Array} runs - payrollIndex runs (loadActiveLocks)
 * @returns {string|null} warning message, or null
 */
export const paidPeriodWarning = (employeeId, effectiveDate, runs) => {
  const dateYMD = toYMDString(effectiveDate);
  if (!dateYMD || !Array.isArray(runs)) return null;
  const covered = runs.some(
    (r) =>
      r &&
      (r.status === 'pending-approval' || r.status === 'completed') &&
      r.periodStart &&
      r.periodEnd &&
      Array.isArray(r.employeeIds) &&
      r.employeeIds.map(String).includes(String(employeeId)) &&
      r.periodStart <= dateYMD &&
      dateYMD <= r.periodEnd,
  );
  return covered
    ? 'This date is already paid — the entry will stay Pending until you re-date it into an open cycle.'
    : null;
};

/**
 * C14 — identity fields are immutable once consumed: while consumedRunId is
 * set, `type` / `employeeId` / `effectiveDate` may not change (void and
 * recreate instead). `type` decides which array the peso lands in; a flip
 * would leave a stale embedded copy paying the opposite sign.
 *
 * @param {Object} existing - the stored doc (needs consumedRunId + the 3 fields)
 * @param {Object} updates - the fields being written
 * @returns {string|null} error message, or null when valid
 */
export const validateImmutable = (existing, updates) => {
  if (!existing || existing.consumedRunId == null) return null;
  const frozen = ['type', 'employeeId', 'effectiveDate'];
  for (const f of frozen) {
    const next = f === 'effectiveDate' ? toYMDString(updates[f]) : updates[f];
    const prev = f === 'effectiveDate' ? toYMDString(existing[f]) : existing[f];
    if (updates[f] !== undefined && next !== prev) {
      return `This entry is in a payroll run — ${f} cannot change. Delete it and create a new entry instead.`;
    }
  }
  return null;
};

// ============================================================
// C24 date clamps (pure — exported for the Tier-1 suite)
// ============================================================

/**
 * C24 — Adjustments-page clamp: previous cycle start … current cycle end,
 * from the company's pay calendar (never calendar months — a cycle can cross
 * one). Undefined bounds when the calendar can't resolve (no picker clamp).
 *
 * @param {Array} cutoffStartDays - the company's two cutoff start days
 * @param {string} todayYMD - 'YYYY-MM-DD'
 * @returns {{min: string|undefined, max: string|undefined}}
 */
export const pageDateClamp = (cutoffStartDays, todayYMD) => {
  const cur = currentPeriod(cutoffStartDays, todayYMD);
  const periods = officialPeriods(cutoffStartDays, todayYMD, { monthsBack: 1, monthsForward: 1 });
  if (!cur || periods.length === 0) return { min: undefined, max: undefined };
  const idx = periods.findIndex((p) => p.start === cur.start);
  const prev = idx > 0 ? periods[idx - 1] : cur;
  return { min: prev.start, max: cur.end };
};

/**
 * C24 — Adjustments-step clamp: the run's period, narrowed to the selected employee's
 * tenure (an employee ending mid-cycle must not default past their endDate).
 * 'YYYY-MM-DD' string compares throughout (C2 — inputs absorbed raw).
 *
 * @param {*} periodStart - Timestamp | Date | 'YYYY-MM-DD'
 * @param {*} periodEnd - Timestamp | Date | 'YYYY-MM-DD'
 * @param {Object} [employment] - { startDate?, endDate? } of the selected employee
 * @returns {{min: string, max: string}}
 */
export const runDateClamp = (periodStart, periodEnd, employment) => {
  const pStart = toYMDString(periodStart);
  const pEnd = toYMDString(periodEnd);
  if (!employment) return { min: pStart, max: pEnd };
  const startYMD = toYMDString(employment.startDate);
  const endYMD = toYMDString(employment.endDate);
  return {
    min: startYMD && startYMD > pStart ? startYMD : pStart,
    max: endYMD && endYMD < pEnd ? endYMD : pEnd,
  };
};

// ============================================================
// Run-embedding helpers (pure — exported for the Tier-1 suite).
// Both are called INSIDE setPayrollRun updaters on `prev` (impl-review #2):
// the CL-001 resume refresh, the C14 rebuild, and the C13 merge all write
// `employees`, so a wholesale write of a pre-await snapshot would silently
// revert whichever writer landed in between.
// ============================================================

/**
 * C14 resume rebuild — refresh a run's embedded adjustment copies from the
 * LIVE standalone docs. Live doc wins on amount/description/category; entries
 * are DROPPED when the link no longer points at this run, the runner excluded
 * them, they were voided, or they drifted outside the cycle/tenure bounds.
 * Duplicated sourceIds are deduped (first embedded copy wins). Legacy entries
 * without a sourceId are untouched.
 *
 * Tenure prefers `ctx.employmentById` — the LIVE employee documents (impl-review
 * r2 #1): the run's embedded `emp.employment` is a snapshot as of the last save,
 * so an endDate HR shortened mid-draft would never fire the drop and C5 would
 * abort every save. The embedded snapshot is only the fallback.
 *
 * @param {Array} employees - the run's employees (embedded earnings/deductions)
 * @param {Object} ctx - { liveById: Map, excludedIds: Set, periodStart, periodEnd: 'YYYY-MM-DD',
 *                        employmentById?: Object }
 * @returns {{ employees: Array, refreshed: number, dropped: number }}
 */
export const rebuildRunAdjustments = (employees, { liveById, excludedIds, periodStart, periodEnd, employmentById }) => {
  let refreshed = 0;
  let dropped = 0;
  const seenSourceIds = new Set();
  const next = (employees || []).map((emp) => {
    const employment =
      (employmentById && employmentById[String(emp.employeeId || emp.id)]) || emp.employment || {};
    const rebuildList = (list) => {
      const out = [];
      (list || []).forEach((a) => {
        if (!a.sourceId) { out.push(a); return; } // legacy ad-hoc copy — untouched
        if (seenSourceIds.has(a.sourceId)) { dropped += 1; return; } // dedupe (C14)
        seenSourceIds.add(a.sourceId);
        const live = liveById.get(a.sourceId);
        if (!live || excludedIds.has(a.sourceId) || live.metadata?.isDeleted) { dropped += 1; return; }
        const inCycle = periodStart <= live.effectiveDate && live.effectiveDate <= periodEnd;
        if (!inCycle || validateTenure(live.effectiveDate, employment)) { dropped += 1; return; }
        if (live.amount !== a.amount || live.description !== a.description || live.category !== a.category) {
          refreshed += 1;
        }
        out.push({ ...a, amount: live.amount, description: live.description, category: live.category });
      });
      return out;
    };
    return {
      ...emp,
      ...(emp.earnings ? { earnings: { ...emp.earnings, adjustments: rebuildList(emp.earnings.adjustments) } } : {}),
      ...(emp.deductions ? { deductions: { ...emp.deductions, adjustments: rebuildList(emp.deductions.adjustments) } } : {}),
    };
  });
  return { employees: next, refreshed, dropped };
};

/**
 * C13 Adjustments-step merge — embed pending-in-cycle entries into the run: pending +
 * inside tenure + employee in the Step-2 selection + not excluded + not
 * already embedded. Entries whose employee is NOT in the run come back as
 * `outside` for the excluded-employee notice.
 *
 * Tenure prefers `employmentById` (live employee docs — impl-review r2 #1),
 * falling back to the run's embedded snapshot.
 *
 * @param {Object} run - { employees, excludedAdjustmentIds }
 * @param {Array} pending - from fetchPendingInCycle
 * @param {Set} selectedIds - the Step-2 employee selection
 * @param {Object} [employmentById] - live employment keyed by employeeId
 * @returns {{ employees: ?Array, outside: Array }} employees is null when nothing embeds
 */
export const mergePendingIntoRun = (run, pending, selectedIds, employmentById) => {
  const excluded = new Set(run.excludedAdjustmentIds || []);
  const inRun = new Map(
    (run.employees || [])
      .filter((emp) => selectedIds.has(emp.employeeId || emp.id))
      .map((emp) => [String(emp.employeeId || emp.id), emp]),
  );
  const embedded = new Set();
  (run.employees || []).forEach((emp) => {
    [...(emp.earnings?.adjustments || []), ...(emp.deductions?.adjustments || [])]
      .forEach((a) => a.sourceId && embedded.add(a.sourceId));
  });

  const outside = [];
  const toEmbed = [];
  (pending || []).forEach((p) => {
    if (excluded.has(p.id) || embedded.has(p.id)) return;
    const emp = inRun.get(String(p.employeeId));
    if (!emp) {
      outside.push(p);
      return;
    }
    const employment =
      (employmentById && employmentById[String(p.employeeId)]) || emp.employment || {};
    if (validateTenure(p.effectiveDate, employment)) return; // C12a/C13
    toEmbed.push(p);
  });
  if (toEmbed.length === 0) return { employees: null, outside };

  const employees = run.employees.map((emp) => {
    const mine = toEmbed.filter((p) => String(p.employeeId) === String(emp.employeeId || emp.id));
    if (mine.length === 0) return emp;
    const next = { ...emp };
    mine.forEach((p) => {
      const arr = p.type === 'earning' ? 'earnings' : 'deductions';
      next[arr] = {
        ...next[arr],
        adjustments: [
          ...(next[arr]?.adjustments || []),
          {
            sourceId: p.id,
            type: p.type,
            category: p.category,
            description: p.description,
            amount: p.amount,
            effectiveDate: p.effectiveDate,
            addedAt: p.createdAt || new Date(),
            updatedAt: p.updatedAt || null,
            addedBy: p.createdBy || null,
          },
        ],
      };
    });
    return next;
  });
  return { employees, outside };
};

// ============================================================
// CRUD (all writes flow through here — C11)
// ============================================================

/**
 * The ONE adjustment document literal (PRD_BulkAdjustments C2/C3). Every
 * creation path — single-add, wizard ad-hoc, bulk — builds its document here,
 * so the shapes cannot drift: there is no second literal to drift from. Pure
 * apart from serverTimestamp(); exported for the Tier-1 suite, which pins the
 * exact key list (a field added here needs a fresh payroll-side inertness
 * review — see C3).
 *
 * `batchId` is the one bulk-only field: an opaque group handle read solely by
 * the batch delete (C8/C9), null on every non-bulk create. It carries no
 * privilege and is never displayed. Legacy documents have no `batchId` key at
 * all — absent ≡ null for every consumer, and no query may filter on
 * `batchId == null` (Firestore does not match a missing field).
 *
 * `thirteenthMonthYear` (PRD_ThirteenthMonthPay C11) is the year a generated
 * 13th month entry pays for, null everywhere else; documents written before it
 * have no key, read the same way.
 *
 * @param {Object} data - { employeeId, type, category, description, amount, effectiveDate, thirteenthMonthYear? }
 * @param {string} userId - author uid
 * @param {string|null} [batchId] - the bulk batch id, null outside bulk
 * @returns {Object} the document to write
 */
export const buildAdjustmentDoc = (data, userId, batchId = null) => ({
  employeeId: String(data.employeeId),
  type: data.type,
  category: data.category,
  description: data.description || '',
  amount: data.amount,
  effectiveDate: toYMDString(data.effectiveDate), // C2 — never a raw Date/Timestamp
  consumedRunId: null, // written only server-side thereafter (C3/C5/C25)
  consumedAt: null,
  createdAt: serverTimestamp(),
  createdBy: userId,
  updatedAt: null,
  updatedBy: null,
  metadata: { isDeleted: false },
  batchId,
  // PRD_ThirteenthMonthPay C11 — the year a generated 13th month entry pays
  // for; null on every other entry. Read only by the 13th month generator.
  thirteenthMonthYear: data.thirteenthMonthYear ?? null,
});

/**
 * Create a standalone adjustment. Hard-validates tenure + amount (C12a/b);
 * the caller surfaces paidPeriodWarning separately (it never blocks).
 *
 * @param {string} companyId
 * @param {Object} data - { employeeId, type, category, description, amount, effectiveDate }
 * @param {Object} employment - the employee's current employment dates (C12a)
 * @param {string} userId - author uid
 * @returns {Promise<string>} the new adjustment id
 */
export const createAdjustment = async (companyId, data, employment, userId) => {
  const tenureError = validateTenure(data.effectiveDate, employment);
  if (tenureError) throw new Error(tenureError);
  const amountError = validateAmount(data.amount);
  if (amountError) throw new Error(amountError);

  const ref = await addDoc(adjustmentsCollection(companyId), buildAdjustmentDoc(data, userId, null));
  return ref.id;
};

/**
 * Update an adjustment. Re-reads the STORED doc + the live payroll index first
 * (impl-review #4): the caller's copy can be minutes old, and a since-claimed
 * (even paid) entry must not be mutated off a stale editable=true. Then re-runs
 * the C12 hard blocks against the employee's CURRENT dates (save-time
 * revalidation is how employment-date changes take effect) and enforces C14
 * immutability. Never touches the consumption link. A millisecond-wide race
 * between the re-read and the write remains — the rules pin still prevents any
 * double payment; this closes the human-scale stale-tab window.
 *
 * @param {string} companyId
 * @param {string} adjustmentId
 * @param {Object} updates - { type?, category?, description?, amount?, effectiveDate?, employeeId? }
 * @param {Object} employment - the employee's current employment dates
 * @param {string} userId
 */
export const updateAdjustment = async (
  companyId, adjustmentId, updates, employment, userId,
) => {
  const [existing, runs] = await Promise.all([
    getAdjustment(companyId, adjustmentId),
    loadActiveLocks(companyId), // throws on read error → edit fails closed
  ]);
  const { status, editable } = resolveAdjustmentStatus(existing.consumedRunId, runs);
  if (!editable) {
    // Distinct copy per state (impl-review r2 #6): a LINK_BROKEN entry is the
    // healable index-write-failed case — "already paid" would gaslight the
    // runner who is looking at live edit buttons in the Adjustments step.
    throw new Error(status === ADJ_STATUS.LINK_BROKEN
      ? 'This entry\'s payroll link can\'t be verified right now — save the payroll draft again to repair it, then retry.'
      : 'This entry is already paid — it can no longer be edited.');
  }
  const immutableError = validateImmutable(existing, updates);
  if (immutableError) throw new Error(immutableError);
  const nextDate = updates.effectiveDate !== undefined ? updates.effectiveDate : existing.effectiveDate;
  const tenureError = validateTenure(nextDate, employment);
  if (tenureError) throw new Error(tenureError);
  const nextAmount = updates.amount !== undefined ? updates.amount : existing.amount;
  const amountError = validateAmount(nextAmount);
  if (amountError) throw new Error(amountError);

  const write = { updatedAt: serverTimestamp(), updatedBy: userId };
  ['type', 'category', 'description', 'amount', 'employeeId'].forEach((f) => {
    if (updates[f] !== undefined) write[f] = f === 'employeeId' ? String(updates[f]) : updates[f];
  });
  if (updates.effectiveDate !== undefined) {
    write.effectiveDate = toYMDString(updates.effectiveDate); // C2
  }
  await updateDoc(doc(adjustmentsCollection(companyId), adjustmentId), write);
};

/**
 * Void (soft-delete, C18). Blocked when the resolved status is Paid or
 * Link-broken — allowed for Pending and In-draft-run (the draft copy is
 * dropped by the C14 resume rebuild + the C5 isDeleted abort; no run-side
 * write, which the clerk couldn't make anyway). Re-reads the doc + the live
 * payroll index itself (impl-review #4) — a stale tab must not void an entry
 * that was paid after the page loaded.
 *
 * @param {string} companyId
 * @param {string} adjustmentId
 * @param {string} userId
 */
export const voidAdjustment = async (companyId, adjustmentId, userId) => {
  const [fresh, runs] = await Promise.all([
    getAdjustment(companyId, adjustmentId),
    loadActiveLocks(companyId), // throws on read error → void fails closed
  ]);
  const { status, editable } = resolveAdjustmentStatus(fresh.consumedRunId, runs);
  if (!editable) {
    throw new Error(status === ADJ_STATUS.LINK_BROKEN
      ? 'This entry\'s payroll link can\'t be verified right now — save the payroll draft again to repair it, then retry.'
      : 'This entry is already paid — it can no longer be deleted.');
  }
  await updateDoc(doc(adjustmentsCollection(companyId), adjustmentId), {
    'metadata.isDeleted': true,
    updatedAt: serverTimestamp(),
    updatedBy: userId,
  });
};

// ============================================================
// Bulk (PRD_BulkAdjustments C4/C6/F1). The partition and the copy helpers are
// pure — exported for the Tier-1 suite, which pins F1's strings exactly.
// ============================================================

/**
 * C4 — partition a selection for a bulk create. Tenure decides SKIP (the one
 * skip class, via validateTenure — the employee is dropped, the rest are
 * still created); already-paid is an ADVISORY count over the KEPT employees
 * only (via paidPeriodWarning — a tenure-skipped employee is never also
 * counted as already-paid). Nothing blocks.
 *
 * @param {Array} selection - the page's projected employees ({ employeeId, startDate, endDate, … })
 * @param {*} effectiveDate - Timestamp | Date | 'YYYY-MM-DD'
 * @param {Array} runs - payrollIndex runs (loadActiveLocks)
 * @returns {{ toCreate: Array, skipped: Array, alreadyPaid: number }}
 */
export const partitionBulkSelection = (selection, effectiveDate, runs) => {
  const toCreate = [];
  const skipped = [];
  (selection || []).forEach((emp) => {
    (validateTenure(effectiveDate, emp) ? skipped : toCreate).push(emp);
  });
  const alreadyPaid = toCreate.filter(
    (emp) => paidPeriodWarning(emp.employeeId, effectiveDate, runs) !== null,
  ).length;
  return { toCreate, skipped, alreadyPaid };
};

/**
 * C1 — the picker's eligibility predicate: employees employed on the effective
 * date. Reads the page's FLAT projection ({ startDate, endDate } at the top
 * level) through validateTenure — NOT employeeFilters' isEmployeeActiveOnDate,
 * which reads employee.employment.* and would return true for every projected
 * employee (C4: the fail-open trap).
 *
 * @param {Array} employees - the page's projected employees
 * @param {*} effectiveDate - Timestamp | Date | 'YYYY-MM-DD'
 * @returns {Array} the employed-on-that-date subset
 */
export const eligibleForDate = (employees, effectiveDate) =>
  (employees || []).filter((e) => !validateTenure(effectiveDate, e));

// F1 — the picker's no-date state (C2). A constant, asserted with toBe.
export const PICKER_NO_DATE = 'Choose an effective date first.';

/**
 * F1 — the preview block's lines, in order: line 1 whenever anything is kept,
 * lines 2 and 3 only when their count is non-zero; all skipped → the single
 * blocking message instead; an empty partition → []. The total is the SIGNED
 * value through formatPeso (U+2212 on a deduction) — a line that cannot tell
 * an ₱18,000 bonus from an ₱18,000 deduction only half-works.
 *
 * @param {{ toCreate: Array, skipped: Array, alreadyPaid: number }} partition
 * @param {number} amount - the shared per-entry amount
 * @param {string} type - 'earning' | 'deduction'
 * @returns {Array<string>}
 */
export const bulkPreviewLines = ({ toCreate, skipped, alreadyPaid }, amount, type) => {
  const n = toCreate.length;
  const m = skipped.length;
  if (n === 0 && m === 0) return [];
  if (n === 0) {
    return [
      m === 1
        ? 'No entries to create — the 1 selected employee is outside their employment dates.'
        : `No entries to create — all ${m} selected employees are outside their employment dates.`,
    ];
  }
  const total = (Number(amount) || 0) * n;
  const lines = [
    `Creating ${n} ${n === 1 ? 'entry' : 'entries'} · ${formatPeso(type === 'earning' ? total : -total)}`,
  ];
  if (m > 0) lines.push(`${m} skipped (outside their employment dates)`);
  if (alreadyPaid > 0) {
    lines.push(
      `${alreadyPaid} already paid for this date — ${alreadyPaid === 1 ? 'that entry' : 'those entries'} will stay Pending`,
    );
  }
  return lines;
};

// Column → the value the CELL shows, lower-cased for strings (Status sorts its
// code; the codes happen to order like the labels). Sorting the raw field reads
// unsorted next to the rendered cell: amount is stored unsigned and shown
// signed, category is a code shown as a label, a missing employee is shown as
// "Employee <id>". Known and deliberately unhandled: a stored category or a
// sortField equal to an Object.prototype key ('constructor', '__proto__') is
// looked up on these plain objects — unreachable from the app (both category
// selects are fixed lists; sortField comes from five hardcoded headers), and a
// reload clears it.
const CATEGORY_LABEL = Object.fromEntries(
  [...EARNING_CATEGORIES, ...DEDUCTION_CATEGORIES].map((c) => [c.value, c.label]),
);
const SORT_KEYS = {
  employee: (r, byId) => (byId[r.employeeId]?.fullName || `Employee ${r.employeeId}`).toLowerCase(),
  category: (r) => (CATEGORY_LABEL[r.category] || r.category || '').toLowerCase(),
  amount: (r) => (r.type === 'earning' ? 1 : -1) * (Number(r.amount) || 0),
  effectiveDate: (r) => r.effectiveDate || '',
  status: (r) => r.resolved?.status || '',
};

/**
 * C7/C8 — the page's grouped sort. Unpaid (Pending + In-draft) always sits
 * above everything else; a sort applies WITHIN each block, never across the
 * split. A falsy or unknown sortField reproduces C15's default exactly —
 * unpaid oldest first, the rest newest first, no tie-break. With a sort
 * active, ties break by employeeId so a bulk batch (one category, amount and
 * date across N rows) sorts deterministically.
 *
 * @param {Array} rows - filtered rows ({ employeeId, type, category, amount, effectiveDate, resolved: { status } })
 * @param {string} sortField - '' | 'employee' | 'category' | 'amount' | 'effectiveDate' | 'status'
 * @param {string} sortDirection - 'asc' | 'desc'
 * @param {Object} employeeById - the page's projected employees keyed by id
 * @returns {Array} a new array
 */
export const sortAdjustmentRows = (rows, sortField, sortDirection, employeeById = {}) => {
  const all = rows || [];
  const unpaid = all.filter((r) => isUnpaidStatus(r.resolved?.status));
  const rest = all.filter((r) => !isUnpaidStatus(r.resolved?.status));
  const keyOf = SORT_KEYS[sortField];
  if (!keyOf) {
    return [
      ...unpaid.sort((a, b) => a.effectiveDate.localeCompare(b.effectiveDate)),
      ...rest.sort((a, b) => b.effectiveDate.localeCompare(a.effectiveDate)),
    ];
  }
  const dir = sortDirection === 'desc' ? -1 : 1;
  const cmp = (a, b) => {
    const ka = keyOf(a, employeeById);
    const kb = keyOf(b, employeeById);
    // localeCompare for strings — a code-unit compare puts Ñ after Z, so
    // Ñuñez would sort last ascending.
    const c = typeof ka === 'string' ? ka.localeCompare(kb) : ka - kb;
    if (c !== 0) return c * dir;
    return String(a.employeeId).localeCompare(String(b.employeeId));
  };
  return [...unpaid.sort(cmp), ...rest.sort(cmp)];
};

/**
 * F1 — the delete dialog's batch sentence, or null when the batch has one or
 * fewer live deletable members (at one, "delete all 1" is meaningless and the
 * dialog stays the plain single-delete). "Already paid" is a deliberate
 * simplification for the aggregate — it also covers LINK_BROKEN.
 *
 * @param {number} total - live (non-voided) members of the batch
 * @param {number} live - of those, still deletable (PENDING / IN_DRAFT)
 * @returns {string|null}
 */
export const batchDialogLine = (total, live) => {
  if (!(live > 1)) return null;
  return live === total
    ? `This entry is one of a batch of ${total} — all ${total} can still be deleted.`
    : `This entry is one of a batch of ${total} — ${live} can still be deleted; the rest are already paid.`;
};

// C6 — the same per-commit backstop payrollRunStore.js / payrollCompletionService.js
// use. The pinned SDK documents a 500-write limit per WriteBatch
// (@firebase/firestore 4.8.0, dist/index.d.ts:3213); 450 leaves headroom.
const MAX_BULK_BATCH_OPS = 450;

/**
 * C6 — the ONE chunked writeBatch for both bulk paths. Each op applies itself
 * to the batch it is handed (`batch.set(ref, doc)` for creates,
 * `batch.update(ref, {…})` for voids), so one chunker serves both. Chunks
 * commit sequentially; on the FIRST failure the loop stops and reports — a
 * permission/subscription error would repeat on every later chunk, and a
 * re-submit mints new ids (so committed chunks would be duplicated), so
 * nothing is retried. Committed chunks stay — they carry the batch id, so
 * the batch delete is the undo. The caught error rides along so the caller's
 * toast can say why.
 *
 * @param {Array<function(Object): void>} ops
 * @returns {Promise<{ committed: number, failed: number, error: Error|null }>}
 */
export const commitInChunks = async (ops) => {
  let committed = 0;
  for (let i = 0; i < ops.length; i += MAX_BULK_BATCH_OPS) {
    const chunk = ops.slice(i, i + MAX_BULK_BATCH_OPS);
    const batch = writeBatch(db);
    chunk.forEach((apply) => apply(batch));
    try {
      await batch.commit();
    } catch (err) {
      console.error('[adjustmentService] bulk chunk commit failed:', err);
      return { committed, failed: ops.length - committed, error: err };
    }
    committed += chunk.length;
  }
  return { committed, failed: 0, error: null };
};

/**
 * Bulk create (C6): one ordinary adjustment document per kept employee, all
 * carrying one generated batch id (C3). Takes the partition's KEPT list — the
 * modal partitions once, for the preview and for the submit. Validates the
 * shared amount once (C2 — the 2-dp rule is client-only) and fails the whole
 * submit before any write.
 *
 * @param {string} companyId
 * @param {Array} toCreate - partitionBulkSelection().toCreate
 * @param {Object} data - { type, category, description, amount, effectiveDate }
 * @param {string} userId
 * @returns {Promise<{ batchId: string, created: number, failed: number, error: Error|null }>}
 */
export const createAdjustmentsBulk = async (companyId, toCreate, data, userId) => {
  const amountError = validateAmount(data.amount);
  if (amountError) throw new Error(amountError);
  const col = adjustmentsCollection(companyId);
  const batchId = doc(col).id; // client-side auto-id — no network, no crypto polyfill
  const ops = toCreate.map((emp) => {
    const ref = doc(col);
    const payload = buildAdjustmentDoc({ ...data, employeeId: emp.employeeId }, userId, batchId);
    return (batch) => batch.set(ref, payload);
  });
  const { committed, failed, error } = await commitInChunks(ops);
  return { batchId, created: committed, failed, error };
};

/**
 * PRD_ThirteenthMonthPay C13 — bulk create where each row carries its OWN
 * amount (the 13th month generator). Every row's amount is validated before
 * any write, so one bad row fails the whole submit; then the same one batch id
 * and chunked commit as createAdjustmentsBulk. There is no tenure skip here:
 * the generator only passes rows its ticking rule (C15) allows, with a date set.
 *
 * @param {string} companyId
 * @param {Array<{employeeId: string, amount: number}>} rows
 * @param {Object} data - { type, category, description, effectiveDate, thirteenthMonthYear }
 * @param {string} userId
 * @returns {Promise<{ batchId: string, created: number, failed: number, error: Error|null }>}
 */
export const createAdjustmentsPerRow = async (companyId, rows, data, userId) => {
  rows.forEach((row) => {
    const amountError = validateAmount(row.amount);
    if (amountError) throw new Error(amountError);
  });
  const col = adjustmentsCollection(companyId);
  const batchId = doc(col).id;
  const ops = rows.map((row) => {
    const ref = doc(col);
    const payload = buildAdjustmentDoc(
      { ...data, employeeId: row.employeeId, amount: row.amount },
      userId,
      batchId,
    );
    return (batch) => batch.set(ref, payload);
  });
  const { committed, failed, error } = await commitInChunks(ops);
  return { batchId, created: committed, failed, error };
};

/**
 * C8 — split a batch's live members into still-deletable (PENDING / IN_DRAFT)
 * and frozen (PAID / LINK_BROKEN — parent C18). resolveAdjustmentStatus takes
 * no isDeleted input, so callers pass fetchBatchMembers' output, which has
 * already dropped the voided. Pure — exported for the Tier-1 suite and for
 * the dialog's preview.
 *
 * @param {Array} members - fetchBatchMembers()
 * @param {Array} runs - payrollIndex runs (loadActiveLocks)
 * @returns {{ live: Array, frozen: Array }}
 */
export const partitionBatchMembers = (members, runs) => {
  const live = [];
  const frozen = [];
  (members || []).forEach((m) => {
    (resolveAdjustmentStatus(m.consumedRunId, runs).editable ? live : frozen).push(m);
  });
  return { live, frozen };
};

/**
 * Batch delete (C8/C9) — the undo. Recomputes membership AND status from
 * FRESH reads at confirm time (the dialog's numbers were a preview): a member
 * submitted to pending-approval while the dialog sat open is skipped, not
 * voided — the same stale-tab guard voidAdjustment has. One loadActiveLocks
 * read serves every member; the soft-deletes go through the C6 chunker.
 *
 * @param {string} companyId
 * @param {string} batchId
 * @param {string} userId
 * @returns {Promise<{ voided: number, frozen: number, failed: number, error: Error|null }>}
 */
export const voidAdjustmentBatch = async (companyId, batchId, userId) => {
  const [members, runs] = await Promise.all([
    fetchBatchMembers(companyId, batchId),
    loadActiveLocks(companyId), // throws on read error → the batch void fails closed
  ]);
  const { live, frozen } = partitionBatchMembers(members, runs);
  const col = adjustmentsCollection(companyId);
  const ops = live.map((m) => (batch) =>
    batch.update(doc(col, m.id), {
      'metadata.isDeleted': true,
      updatedAt: serverTimestamp(),
      updatedBy: userId,
    }));
  const { committed, failed, error } = await commitInChunks(ops);
  return { voided: committed, frozen: frozen.length, failed, error };
};

// ============================================================
// Queries (shapes per the PRD's Queries table)
// ============================================================

const snapToRows = (snap) =>
  snap.docs.map((d) => ({ id: d.id, ...d.data() }));

/**
 * One adjustment by id (updateAdjustment/voidAdjustment re-read through this
 * so C14 immutability and C18's status check run on real data, not the
 * caller's possibly-stale copy).
 *
 * @param {string} companyId
 * @param {string} adjustmentId
 * @returns {Promise<Object>} the doc incl. id
 */
export const getAdjustment = async (companyId, adjustmentId) => {
  const snap = await getDoc(doc(adjustmentsCollection(companyId), adjustmentId));
  if (!snap.exists()) throw new Error('This adjustment no longer exists.');
  return { id: snap.id, ...snap.data() };
};

/**
 * Adjustments step / draft resume — pending entries dated inside the cycle (C13).
 * Company-wide; the caller filters to the run's employees + tenure
 * client-side. Bounds are serialized via toYMDString because a fresh run's
 * period is a Date in wizard state (C2 — a Timestamp bound against a string
 * field silently matches nothing).
 *
 * @param {string} companyId
 * @param {*} periodStart - Timestamp | Date | 'YYYY-MM-DD'
 * @param {*} periodEnd - Timestamp | Date | 'YYYY-MM-DD'
 * @returns {Promise<Array>} pending, non-deleted entries in the window
 */
export const fetchPendingInCycle = async (companyId, periodStart, periodEnd) => {
  const snap = await getDocs(
    query(
      adjustmentsCollection(companyId),
      where('consumedRunId', '==', null),
      where('effectiveDate', '>=', toYMDString(periodStart)),
      where('effectiveDate', '<=', toYMDString(periodEnd)),
    ),
  );
  return snapToRows(snap).filter((a) => !a.metadata?.isDeleted);
};

/**
 * The consumed-by-this-run set (C14 resume rebuild). The query — not the run's
 * embedded copies — is the source of truth: only the query reveals an entry
 * whose embedded copy was already pruned.
 *
 * @param {string} companyId
 * @param {string} runId
 * @returns {Promise<Array>}
 */
export const fetchConsumedByRun = async (companyId, runId) => {
  const snap = await getDocs(
    query(adjustmentsCollection(companyId), where('consumedRunId', '==', runId)),
  );
  return snapToRows(snap);
};

/**
 * C9 — a batch's LIVE members: the `batchId` equality query (single-field —
 * no declared index needed), then the house `!isDeleted` filter. Already-
 * voided members are excluded from both the count and the write — a no-op,
 * never a re-void that would overwrite the original void's provenance.
 *
 * @param {string} companyId
 * @param {string} batchId
 * @returns {Promise<Array>}
 */
export const fetchBatchMembers = async (companyId, batchId) => {
  // C3: a null/empty id would match every non-bulk document (they carry an
  // explicit `batchId: null`) — refuse rather than run that query.
  if (!batchId) throw new Error('A batch id is required.');
  const snap = await getDocs(
    query(adjustmentsCollection(companyId), where('batchId', '==', batchId)),
  );
  return snapToRows(snap).filter((a) => !a.metadata?.isDeleted);
};

/**
 * The page's always-visible UNPAID set (C13/C16): unclaimed entries PLUS
 * entries claimed by a run that has not yet paid (a draft — an abandoned or
 * rejected run). Draft ids come from the index payload the page already
 * loads; chunked at Firestore's 30-value `in` cap. Voided entries are kept —
 * the page's "Voided" status filter decides visibility (C16).
 *
 * @param {string} companyId
 * @param {Array} runs - payrollIndex runs (loadActiveLocks)
 * @returns {Promise<Array>} deduped by id
 */
export const fetchUnpaidAdjustments = async (companyId, runs) => {
  const col = adjustmentsCollection(companyId);
  const reads = [getDocs(query(col, where('consumedRunId', '==', null)))];

  const draftIds = (Array.isArray(runs) ? runs : [])
    .filter((r) => r && r.status === 'draft' && r.runId)
    .map((r) => r.runId);
  for (let i = 0; i < draftIds.length; i += 30) {
    reads.push(getDocs(query(col, where('consumedRunId', 'in', draftIds.slice(i, i + 30)))));
  }

  const snaps = await Promise.all(reads);
  const byId = new Map();
  snaps.forEach((snap) => snapToRows(snap).forEach((row) => byId.set(row.id, row)));
  return [...byId.values()];
};

/**
 * PENDING adjustment summary for the dashboard card (BL-076): how many are
 * unpaid and what they net out to.
 *
 * PENDING is the one status that resolves WITHOUT the payroll index —
 * `resolveAdjustmentStatus(null, runs)` returns PENDING for a null link
 * regardless of `runs` — so this deliberately skips the `loadActiveLocks`
 * call that `fetchUnpaidAdjustments` needs. It therefore counts strictly
 * "never claimed by any run", NOT the page's wider unpaid set (which also
 * includes entries stranded on an abandoned draft, ADJ_STATUS.IN_DRAFT).
 * Voided entries are excluded.
 *
 * `net` is free here — the documents are already fetched for the count, so it
 * costs no extra read. It uses the same sign convention as the Adjustments
 * page (`AdjustmentsPage.js` stats): earnings add, deductions subtract.
 *
 * @param {string} companyId
 * @returns {Promise<{count: number, net: number}>}
 */
export const fetchPendingAdjustmentSummary = async (companyId) => {
  const snap = await getDocs(
    query(adjustmentsCollection(companyId), where('consumedRunId', '==', null)),
  );
  const live = snapToRows(snap).filter((row) => row?.metadata?.isDeleted !== true);
  return {
    count: live.length,
    net: live.reduce(
      (sum, r) => sum + (r.type === 'earning' ? Number(r.amount) || 0 : -(Number(r.amount) || 0)),
      0,
    ),
  };
};

/**
 * LIVE employment for a set of employeeIds (impl-review r2 #1) — the C14
 * rebuild and C13 merge must judge tenure against the employee's CURRENT
 * dates, not the run's persisted snapshot. Mirrors savePayrollRun's
 * server-side source (the employee documents); chunked at the 30-value
 * `in` cap. Missing employees are simply absent from the map (callers
 * fall back to the embedded snapshot).
 *
 * @param {string} companyId
 * @param {Array<string>} employeeIds
 * @returns {Promise<Object>} { [employeeId]: employment }
 */
export const fetchEmploymentByIds = async (companyId, employeeIds) => {
  // Filter BEFORE the String() map — String(null) is the truthy 'null' (r3 #6).
  const ids = [...new Set((employeeIds || []).filter((v) => v != null && v !== '').map(String))];
  const col = collection(db, 'companies', companyId, 'employees');
  const reads = [];
  for (let i = 0; i < ids.length; i += 30) {
    reads.push(getDocs(query(col, where('employeeId', 'in', ids.slice(i, i + 30)))));
  }
  const snaps = await Promise.all(reads);
  const map = {};
  snaps.forEach((snap) => snap.docs.forEach((d) => {
    const data = d.data();
    if (data.employeeId != null) map[String(data.employeeId)] = data.employment || {};
  }));
  return map;
};

/**
 * Paid/consumed history for the month picker (C16). Month bounds are
 * 'YYYY-MM-DD' strings (serialized by the caller or absorbed here).
 *
 * @param {string} companyId
 * @param {*} monthStart
 * @param {*} monthEnd
 * @returns {Promise<Array>}
 */
export const fetchAdjustmentsForMonth = async (companyId, monthStart, monthEnd) => {
  const snap = await getDocs(
    query(
      adjustmentsCollection(companyId),
      where('effectiveDate', '>=', toYMDString(monthStart)),
      where('effectiveDate', '<=', toYMDString(monthEnd)),
    ),
  );
  return snapToRows(snap);
};
